import React, { forwardRef } from "react";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  variant?: "default" | "small" | "rounded-md" | "shadow-sm";
  fullWidth?: boolean;
  showLabel?: boolean;
}

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  variant?: "default" | "small" | "rounded-md" | "shadow-sm";
  fullWidth?: boolean;
  showLabel?: boolean;
}

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  variant?: "default" | "small" | "rounded-md" | "shadow-sm";
  fullWidth?: boolean;
  showLabel?: boolean;
  children: React.ReactNode;
}

const baseInputClasses =
  "px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-colors";
const smallInputClasses =
  "px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-colors";
const roundedMdClasses =
  "px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors";
const shadowSmClasses =
  "px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 transition-colors";
const errorClasses = "border-red-300 focus:ring-red-500 focus:border-red-500";

const getInputClasses = (
  hasError: boolean,
  variant: "default" | "small" | "rounded-md" | "shadow-sm",
  fullWidth: boolean,
) => {
  let baseClasses: string;
  switch (variant) {
    case "small":
      baseClasses = smallInputClasses;
      break;
    case "rounded-md":
      baseClasses = roundedMdClasses;
      break;
    case "shadow-sm":
      baseClasses = shadowSmClasses;
      break;
    default:
      baseClasses = baseInputClasses;
  }

  const widthClasses = fullWidth ? "w-full" : "";
  const errorStateClasses = hasError ? errorClasses : "";

  return `${baseClasses} ${widthClasses} ${errorStateClasses}`.trim();
};

const getLabelClasses = (required: boolean) => {
  return `block text-sm font-medium text-gray-700 mb-1 ${required ? 'after:content-["*"] after:ml-1 after:text-red-500' : ""}`;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      required = false,
      variant = "default",
      fullWidth = true,
      showLabel = true,
      className = "",
      ...props
    },
    ref,
  ) => {
    const hasError = !!error;
    const inputClasses = getInputClasses(hasError, variant, fullWidth);

    return (
      <div className="space-y-1">
        {label && showLabel && (
          <label className={getLabelClasses(required)}>{label}</label>
        )}
        <input
          ref={ref}
          className={`${inputClasses} ${className}`}
          {...props}
        />
        {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
        {helperText && !error && (
          <p className="text-sm text-gray-500 mt-1">{helperText}</p>
        )}
      </div>
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      required = false,
      variant = "default",
      fullWidth = true,
      showLabel = true,
      className = "",
      ...props
    },
    ref,
  ) => {
    const hasError = !!error;
    const inputClasses = getInputClasses(hasError, variant, fullWidth);

    return (
      <div className="space-y-1">
        {label && showLabel && (
          <label className={getLabelClasses(required)}>{label}</label>
        )}
        <textarea
          ref={ref}
          className={`${inputClasses} ${className}`}
          {...props}
        />
        {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
        {helperText && !error && (
          <p className="text-sm text-gray-500 mt-1">{helperText}</p>
        )}
      </div>
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      required = false,
      variant = "default",
      fullWidth = true,
      showLabel = true,
      className = "",
      children,
      ...props
    },
    ref,
  ) => {
    const hasError = !!error;
    const inputClasses = getInputClasses(hasError, variant, fullWidth);

    return (
      <div className="space-y-1">
        {label && showLabel && (
          <label className={getLabelClasses(required)}>{label}</label>
        )}
        <select ref={ref} className={`${inputClasses} ${className}`} {...props}>
          {children}
        </select>
        {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
        {helperText && !error && (
          <p className="text-sm text-gray-500 mt-1">{helperText}</p>
        )}
      </div>
    );
  },
);

// Export both components as default for convenience
export default Input;
